import { ProjectFolderConnection, ProjectSettingsResponse } from "../models/projects/ProjectSettingsResponse";

const ENTRA_LOGIN_BASE_URL = "https://login.microsoftonline.com";

/** SharePoint Online connections that carry the Entra identifiers the MSAL flow needs. */
export function getSharePointConnections(
    settings: ProjectSettingsResponse | null | undefined
): ProjectFolderConnection[] {
    return (settings?.projectFolderConnections ?? []).filter(
        (connection) => connection.type === "SharePointOnline" && !!connection.appId && !!connection.tenantId
    );
}

export function toMsalAuthority(tenantId: string): string {
    return `${ENTRA_LOGIN_BASE_URL}/${tenantId}`;
}

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Literal (escaped) host: building this from ENTRA_LOGIN_BASE_URL would leave the dots unescaped.
const AUTHORITY_PATTERN = /^https:\/\/login\.microsoftonline\.com\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

/** Entra app (client) IDs are GUIDs; anything else on the consent dialog URL is rejected. */
export function isValidMsalClientId(value: string | null | undefined): value is string {
    return !!value && GUID_PATTERN.test(value);
}

/** Only tenant-specific authorities on login.microsoftonline.com are accepted (no "common"/"organizations"). */
export function isValidMsalAuthority(value: string | null | undefined): value is string {
    return !!value && AUTHORITY_PATTERN.test(value);
}

/** Inverse of toMsalAuthority; null when the authority is not a valid tenant-specific one. */
export function tenantIdFromMsalAuthority(authority: string): string | null {
    return AUTHORITY_PATTERN.exec(authority)?.[1] ?? null;
}

const LOGIN_HINT_PATTERN = /^[^\s@/?#]+@[^\s@/?#]+\.[^\s@/?#]+$/;
const LOGIN_HINT_MAX_LENGTH = 254;

/** Loose email-shape check for the optional `login_hint` taken from the consent dialog URL. */
export function isValidMsalLoginHint(value: string | null | undefined): value is string {
    return !!value && value.length <= LOGIN_HINT_MAX_LENGTH && LOGIN_HINT_PATTERN.test(value);
}

/** Stable key for sharing one MSAL client between connections on the same Entra app + tenant. */
export function getMsalClientKey(connection: Pick<ProjectFolderConnection, "appId" | "tenantId">): string {
    return `${connection.tenantId}/${connection.appId}`;
}
